import React, { useState, useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import fartSound from "../assets/fart.mp3";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import UserInfoOverlay from "../components/UserInfoOverlay";
import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

const FONT_SIZES = ["Default", "Small", "Large", "Extra Large"];

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return null;
  const gb = bytes / 1024 ** 3;

  if (gb >= 1) return `${gb.toFixed(1)} GB`;

  return `${Math.round(bytes / 1024 ** 2)} MB`;
};

// Pill toggle
const Toggle = ({ on, onClick }) => (
  <button
    className={`relative h-6 w-12 shrink-0 rounded-full transition-colors ${
      on ? "bg-blue-500" : "bg-gray-600"
    }`}
    type="button"
    onClick={onClick}
  >
    <div
      className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${
        on ? "translate-x-7" : "translate-x-1"
      }`}
    />
  </button>
);

// Quick-access tile
const QuickTile = ({ icon, label, state, stateColor, onClick }) => (
  <button
    className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm transition-colors hover:bg-white/[0.1]"
    type="button"
    onClick={onClick}
  >
    <i className={`fas ${icon} text-lg text-gray-300`} />
    <span className="text-xs text-gray-300">{label}</span>
    {state ? (
      <span className={`text-xs font-semibold ${stateColor}`}>{state}</span>
    ) : null}
  </button>
);

// Glass setting row
const Row = ({
  icon,
  iconText,
  label,
  subtitle,
  onClick,
  danger,
  children,
}) => {
  const content = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {iconText ? (
          <span
            className={`w-5 text-center text-lg ${danger ? "text-red-400" : "text-gray-400"}`}
          >
            {iconText}
          </span>
        ) : (
          <i
            className={`fas ${icon} w-5 text-center ${
              danger ? "text-red-400" : "text-gray-400"
            }`}
          />
        )}
        <div className="min-w-0">
          <div className={`text-sm ${danger ? "text-red-400" : "text-white"}`}>
            {label}
          </div>
          {subtitle ? (
            <div
              className={`text-xs ${danger ? "text-red-400/70" : "text-gray-500"}`}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
      {children}
    </>
  );
  const className = `flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm transition-colors ${
    onClick ? "cursor-pointer hover:bg-white/[0.08]" : ""
  }`;

  if (onClick) {
    return (
      <div
        className={className}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={activateOnEnterOrSpace}
      >
        {content}
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};

const WebPlayerSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  // Settings state
  const [fontSize, setFontSize] = useState("Default");
  const [closedCaptions, setClosedCaptions] = useState(false);
  const [autoplayTrailers, setAutoplayTrailers] = useState(true);
  const [autoClearCache, setAutoClearCache] = useState(true);
  const [appVersion] = useState("v1.1.5");
  const [availableStorage, setAvailableStorage] = useState("a Gazillion");
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [showFontSizeModal, setShowFontSizeModal] = useState(false);

  // Reopen the user-info overlay when returning from the avatars page
  useEffect(() => {
    if (sessionStorage.getItem("reopenUserInfo") === "true") {
      sessionStorage.removeItem("reopenUserInfo");
      setShowUserInfo(true);
    }
  }, []);

  useEffect(() => {
    const savedSettings = localStorage.getItem("webPlayerSettings");

    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      const savedFontSize = settings.fontSize || "Default";

      setFontSize(savedFontSize);
      setClosedCaptions(settings.closedCaptions || false);
      setAutoplayTrailers(settings.autoplayTrailers !== false);
      setAutoClearCache(settings.autoClearCache !== false);
      applyFontSizeClass(savedFontSize);
    } else {
      applyFontSizeClass("Default");
    }
  }, []);

  // Best-effort real device storage estimate (falls back to the joke)
  useEffect(() => {
    if (navigator.storage?.estimate) {
      navigator.storage
        .estimate()
        .then(({ quota, usage }) => {
          const free = formatBytes((quota || 0) - (usage || 0));

          if (free) setAvailableStorage(free);
        })
        .catch(() => {});
    }
  }, []);

  // Close the font-size picker on Escape
  useEffect(() => {
    if (!showFontSizeModal) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowFontSizeModal(false);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showFontSizeModal]);

  const applyFontSizeClass = (size) => {
    document.documentElement.classList.remove(
      "text-size-small",
      "text-size-default",
      "text-size-large",
      "text-size-extra-large"
    );

    switch (size) {
      case "Small":
        document.documentElement.classList.add("text-size-small");
        break;
      case "Large":
        document.documentElement.classList.add("text-size-large");
        break;
      case "Extra Large":
        document.documentElement.classList.add("text-size-extra-large");
        break;
      default:
        document.documentElement.classList.add("text-size-default");
        break;
    }
  };

  const persist = (next) => {
    const settings = {
      fontSize,
      closedCaptions,
      autoplayTrailers,
      autoClearCache,
      ...next,
    };

    localStorage.setItem("webPlayerSettings", JSON.stringify(settings));
    window.dispatchEvent(new Event("webPlayerSettingsUpdated"));
  };

  const handleFontSizeChange = (size) => {
    setFontSize(size);
    applyFontSizeClass(size);
    persist({ fontSize: size });
  };

  const cycleFontSize = () => {
    const next =
      FONT_SIZES[(FONT_SIZES.indexOf(fontSize) + 1) % FONT_SIZES.length];

    handleFontSizeChange(next);
  };

  const handleClosedCaptionsToggle = () => {
    const newValue = !closedCaptions;

    setClosedCaptions(newValue);
    persist({ closedCaptions: newValue });
    toast.success(
      newValue
        ? "Closed captions enabled - Captions will auto-enable in video player"
        : "Closed captions disabled - Manual control in video player"
    );
  };

  const handleAutoplayTrailersToggle = () => {
    const newValue = !autoplayTrailers;

    setAutoplayTrailers(newValue);
    persist({ autoplayTrailers: newValue });
  };

  const handleAutoClearCacheToggle = () => {
    const newValue = !autoClearCache;

    setAutoClearCache(newValue);
    persist({ autoClearCache: newValue });
  };

  const handleClearCache = () => {
    const keysToRemove = Object.keys(localStorage).filter((key) =>
      /cache/i.test(key)
    );

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    toast.success("Cache cleared");
  };

  const handleSpeedTest = () => {
    navigate("/web-player/speed-test", { state: { profile, playlist } });
  };

  const handleLunovaSupport = () => {
    window.open("https://lunova.app/support", "_blank", "noopener");
  };

  const handleLogOutAndReset = () => {
    sessionStorage.clear();
    localStorage.clear();
    toast.info("All data cleared, logging out...");
    navigate("/web-player/login");
  };

  const handleClearRecentlyWatched = async () => {
    try {
      const recentlyWatchedResponse = await apiService.getRecentlyWatched(
        playlist.id
      );

      if (
        recentlyWatchedResponse?.data &&
        Array.isArray(recentlyWatchedResponse.data)
      ) {
        const deletePromises = recentlyWatchedResponse.data.map(
          async (item) => {
            try {
              let mediaType = "movie";

              if (item.type) {
                mediaType = item.type;
              } else if (item.series_id || item.seriesId) {
                mediaType = "series";
              } else if (item.stream_id || item.streamId) {
                mediaType = "live-tv";
              }

              await apiService.webPlayerRequest(`/v2/recently-watched/delete`, {
                method: "POST",
                body: JSON.stringify({
                  mediaId:
                    item.id ||
                    item.stream_id ||
                    item.series_id ||
                    item.movie_id,
                  mediaType,
                }),
              });
            } catch (deleteError) {
              console.error(`Failed to delete item ${item.id}:`, deleteError);
            }
          }
        );

        await Promise.all(deletePromises);
      }

      Object.keys(localStorage)
        .filter(
          (key) =>
            key.includes("watched") ||
            key.includes("progress") ||
            key.includes("recent")
        )
        .forEach((key) => localStorage.removeItem(key));

      toast.success("Watched history cleared and homepage reset to default");
    } catch (error) {
      console.error("Error clearing recently watched:", error);
      toast.error(`Failed to clear watched history: ${error.message}`);
    }
  };

  const quickTiles = [
    {
      icon: "fa-closed-captioning",
      label: "CC",
      state: closedCaptions ? "ON" : "OFF",
      stateColor: closedCaptions ? "text-blue-400" : "text-gray-500",
      onClick: handleClosedCaptionsToggle,
    },
    {
      icon: "fa-film",
      label: "Trailers",
      state: autoplayTrailers ? "ON" : "OFF",
      stateColor: autoplayTrailers ? "text-blue-400" : "text-gray-500",
      onClick: handleAutoplayTrailersToggle,
    },
    {
      icon: "fa-sync-alt",
      label: "Auto Cache",
      state: autoClearCache ? "ON" : "OFF",
      stateColor: autoClearCache ? "text-blue-400" : "text-gray-500",
      onClick: handleAutoClearCacheToggle,
    },
    {
      icon: "fa-font",
      label: "Font",
      state: fontSize,
      stateColor: "text-gray-400",
      onClick: cycleFontSize,
    },
    {
      icon: "fa-tachometer-alt",
      label: "Speed",
      state: "Test",
      stateColor: "text-gray-400",
      onClick: handleSpeedTest,
    },
    {
      icon: "fa-user",
      label: "Account",
      state: "Open",
      stateColor: "text-gray-400",
      onClick: () => setShowUserInfo(true),
    },
  ];

  const chevron = <i className="fas fa-chevron-right text-sm text-gray-400" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-950 to-black">
      <UserInfoOverlay
        isOpen={showUserInfo}
        playlist={playlist}
        profile={profile}
        onClose={() => setShowUserInfo(false)}
      />

      {/* Font size picker overlay */}
      {showFontSizeModal && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center"
          role="presentation"
          onClick={() => setShowFontSizeModal(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />
          <div
            className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/15 bg-gray-900/40 p-6 shadow-2xl ring-1 ring-inset ring-white/5 backdrop-blur-2xl"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-center text-lg font-bold text-white">
              Font Size
            </h3>
            <div className="space-y-2">
              {FONT_SIZES.map((size) => {
                const active = size === fontSize;

                return (
                  <button
                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${
                      active
                        ? "border-blue-500/50 bg-blue-500/15 text-white"
                        : "border-white/10 bg-white/[0.04] text-gray-200 hover:bg-white/[0.08]"
                    }`}
                    key={size}
                    type="button"
                    onClick={() => {
                      handleFontSizeChange(size);
                      setShowFontSizeModal(false);
                    }}
                  >
                    <span>{size}</span>
                    {active ? (
                      <i className="fas fa-check text-blue-400" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <MobileMenu />

      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          webPlayerStorage.clearAll();
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      <LeftMenu
        activeMenuItem="settings"
        playlist={playlist}
        profile={profile}
      />

      {/* Main Content */}
      <div className="ml-0 lg:ml-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="pt-16 lg:pt-12 pb-4 lg:pb-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Settings
          </h1>
        </div>

        <div className="mx-auto max-w-5xl space-y-8">
          {/* Quick Access */}
          <section>
            <h2 className="mb-3 text-xs font-semibold tracking-[0.2em] text-gray-400">
              QUICK ACCESS
            </h2>
            <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
              {quickTiles.map((tile) => (
                <QuickTile key={tile.label} {...tile} />
              ))}
            </div>
          </section>

          {/* Account */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">Account</h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Row
                icon="fa-user-circle"
                label="User Settings"
                onClick={() => setShowUserInfo(true)}
              >
                {chevron}
              </Row>
            </div>
          </section>

          {/* Display */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">Display</h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Row
                iconText="A"
                label="Font Size"
                onClick={() => setShowFontSizeModal(true)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">{fontSize}</span>
                  <i className="fas fa-chevron-right text-sm text-gray-400" />
                </div>
              </Row>
              <Row icon="fa-closed-captioning" label="Closed Captions">
                <Toggle
                  on={closedCaptions}
                  onClick={handleClosedCaptionsToggle}
                />
              </Row>
              <Row
                icon="fa-film"
                label="Autoplay Trailers"
                onClick={handleAutoplayTrailersToggle}
              >
                <Toggle
                  on={autoplayTrailers}
                  onClick={handleAutoplayTrailersToggle}
                />
              </Row>
            </div>
          </section>

          {/* Storage */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">Storage</h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Row
                icon="fa-trash-alt"
                label="Clear Cache"
                onClick={handleClearCache}
              >
                {chevron}
              </Row>
              <Row
                icon="fa-sync-alt"
                label="Auto Clear Cache"
                onClick={handleAutoClearCacheToggle}
              >
                <Toggle
                  on={autoClearCache}
                  onClick={handleAutoClearCacheToggle}
                />
              </Row>
              <Row icon="fa-hdd" label="Available Storage">
                <span className="text-sm text-gray-400">
                  {availableStorage}
                </span>
              </Row>
            </div>
          </section>

          {/* About & Support */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">
              About &amp; Support
            </h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Row
                icon="fa-info-circle"
                label="App Version"
                onClick={() => new Audio(fartSound).play()}
              >
                <span className="text-sm text-gray-400">{appVersion}</span>
              </Row>
              <Row
                icon="fa-question-circle"
                label="Lunova Support"
                subtitle="lunova.app/support"
                onClick={handleLunovaSupport}
              >
                {chevron}
              </Row>
              <Row
                icon="fa-tachometer-alt"
                label="Speed Test"
                onClick={handleSpeedTest}
              >
                {chevron}
              </Row>
            </div>
          </section>

          {/* Danger Zone */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-red-500">
              Danger Zone
            </h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Row
                danger
                icon="fa-exclamation-triangle"
                label="Log Out & Reset"
                subtitle="Clears all app data and logs you out"
                onClick={handleLogOutAndReset}
              >
                {chevron}
              </Row>
              <Row
                danger
                icon="fa-history"
                label="Clear Recently Watched"
                subtitle="Clears watched history; resets homepage to default"
                onClick={handleClearRecentlyWatched}
              >
                {chevron}
              </Row>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WebPlayerSettings;
